import { formatDate } from "../../constants-ticker";
import {
  getAmericanFootballMatchData,
  getAmericanFootballTeamRecord,
} from "../sports/tickerSportUtils";

/**
 * Tarjeta de resultados/próximos para football americano (sport_code AMFO).
 *
 * La información del deporte se toma del bloque `football_data` del encuentro.
 *
 * Estructura común a próximos y jugados:
 * - Fila superior: nombre + logo de cada equipo con el marcador al centro
 *   (o "VS" en próximos).
 * - Fila inferior: récord ganados-perdidos (W-L) de cada equipo a los costados y,
 *   al centro, la fecha/hora (próximos) o el estado ("Finalizado" / periodo).
 */
export default function TickerFootballResultCard({ result = {}, league = {} }) {
  const isNext = result.class === "clarosports-next-game";
  const isProgress = result.class === "clarosports-in-progress-game";
  const isEnd = result.class === "clarosports-finished-game";
  const isPlaying = isProgress || isEnd;

  const data = getAmericanFootballMatchData(result);
  const leftStats = data.left_stats ?? data.leftStats ?? {};
  const rightStats = data.right_stats ?? data.rightStats ?? {};

  const teams = {
    home: {
      name: result.left_short_name || result.left_name || "Local",
      teamClass: result.left_class,
      image: result.left_image,
      score: result.left_score ?? leftStats?.linescoreTotals?.score,
      record: getAmericanFootballTeamRecord(leftStats),
    },
    away: {
      name: result.right_short_name || result.right_name || "Visita",
      teamClass: result.right_class,
      image: result.right_image,
      score: result.right_score ?? rightStats?.linescoreTotals?.score,
      record: getAmericanFootballTeamRecord(rightStats),
    },
  };

  const statusLabel = isEnd
    ? "Finalizado"
    : isProgress
      ? (result.period_title || "En vivo")
      : formatDate(result.utc_date);

  const cardClasses = [
    "ticker-result-card",
    "ticker-result-card--baseball",
    "ticker-result-card--football",
    isNext && "ticker-result-card--upcoming",
    isProgress && "ticker-result-card--in-progress",
    isEnd && "ticker-result-card--finished",
  ].filter(Boolean).join(" ");

  // Resalta al ganador cuando el encuentro terminó (clarosports-winner/loser).
  const isWinner = (teamClass) => teamClass === "clarosports-winner";
  // Colorea el marcador según el estatus del equipo, igual que en FOOT/BASE:
  // winner/draw -> "score-ganador" (negro); loser -> "score-perdedor" (gris).
  const scoreClass = (teamClass) =>
    `ticker-base-card__runs ${["clarosports-winner", "clarosports-draw"].includes(teamClass) ? "score-ganador" : "score-perdedor"}`;

  const renderTeam = (team, side) => {
    const logo = team.image && (
      <img
        key="logo"
        src={team.image}
        alt={`logo-${side}`}
        onError={(event) => { event.currentTarget.style.visibility = "hidden"; }}
      />
    );
    const name = <b key="name">{team.name}</b>;

    // Local (izquierda): nombre + logo. Visita (derecha): logo + nombre.
    const content = side === "home" ? [name, logo] : [logo, name];

    return (
      <div className={`ticker-base-card__team ticker-base-card__team--${side}${isEnd && isWinner(team.teamClass) ? " ticker-base-card__team--winner" : ""}`}>
        {content}
      </div>
    );
  };

  return (
    <article className={cardClasses}>
      <div className="ticker-base-card__match">
        {renderTeam(teams.home, "home")}

        <div className="ticker-base-card__score-stack">
          {isPlaying ? (
            <>
              <span className={scoreClass(teams.home.teamClass)}>{teams.home.score}</span>
              <span className="ticker-base-card__runs-sep">-</span>
              <span className={scoreClass(teams.away.teamClass)}>{teams.away.score}</span>
            </>
          ) : (
            <span className="ticker-base-card__runs ticker-base-card__runs--vs">VS</span>
          )}
        </div>

        {renderTeam(teams.away, "away")}
      </div>

      <div className="ticker-base-card__detail">
        {/*
          Misma fila para próximos y jugados: récord (W-L) de cada equipo a los
          costados y, al centro, la fecha/hora (próximos) o el estado del
          encuentro ("Finalizado" / periodo en curso).
        */}
        <div className="ticker-base-card__record-row">
          <span className="ticker-base-card__record ticker-base-card__record--home">
            {teams.home.record ? `(${teams.home.record})` : ""}
          </span>
          <span className="ticker-base-card__status">
            {statusLabel}
          </span>
          <span className="ticker-base-card__record ticker-base-card__record--away">
            {teams.away.record ? `(${teams.away.record})` : ""}
          </span>
        </div>
      </div>
    </article>
  );
}
